import {Plural, Trans} from '@lingui/macro';
import _, {concat, isUndefined} from 'lodash';
import React, {FC, useCallback, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useUnmountCancelToken} from '../../../srcCommon/helpers/hookHelpers';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import emptyTag from '../../assets/images/noTags.svg';
import {BoxedIcon} from '../../components/common/boxedIcon';
import {SheetEmoji} from '../../components/common/emoji/sheetEmoji';
import {getTagEmojiHighlight} from '../../helpers/models/highlightHelpers';
import {buildTagSearchUrl, getTagChildrenIds, SearchTagSortsEnum} from '../../helpers/models/tagHelpers';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps} from '../../helpers/reselectHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {HighlightTypesEnum} from '../../models/api/highlightBaseModel';
import {ApiSearchTags, SearchTagModel} from '../../models/api/searchTagModel';
import {TagBulkActionTypesEnum} from '../../models/api/tagBulkActionModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {SettingsTagsUrl} from '../../models/url/settingsUrlModel';
import {fetchApiAsync} from '../../services/http/http';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {tagsBulkAction} from '../../store/data/tags/tagBulkActions';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsResourceDialogListItem} from '../common/dialog/settingsResourceDialogListItem';
import {SettingsResourceListDialog} from '../common/dialog/settingsResourceListDialog';
import {SettingsTagsNestUnderDialogToplineContainer} from './settingsTagsNestUnderDialogToplineContainer';

/*
 * Props.
 */

interface SettingsTagsNestUnderDialogProps extends ResolveDispatchProps<typeof mapDispatchToProps> {
  searchQuery?: string;
  isInArchivedView: boolean;
  tagsToActOn: ReadonlyArray<TagModel>;
  hasAllSelected: boolean;
  tagsUrl: SettingsTagsUrl;
  company: CompanyModel;
  onRequestClose: Handler;
  onNest: Handler;
  onAddTagClick: Handler;
  initialListParentTagId?: number;
}

/*
 * Style.
 */

const StyledBoxedEmoji = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  background-color: ${(p) => p.theme.greys.white};
  border: 1px ${(p) => p.theme.alphas.gray10} solid;
`;

/*
 * Component.
 */

const SettingsTagsNestUnderDialog: FC<SettingsTagsNestUnderDialogProps> = (props) => {
  const theme = useUserTheme();
  const {tagsUrl, company, hasAllSelected, tagsToActOn, initialListParentTagId} = props;
  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [listParentTagId, setListParentTagId] = useState<number | undefined>(initialListParentTagId);
  const cancelToken = useUnmountCancelToken();
  const [searchTags, setSearchTags] = useState<ReadonlyArray<SearchTagModel>>([]);
  const [statusMore, setStatusMore] = useState(AsyncMoreStatusesEnum.NONE);
  const [newParentTag, setNewParentTag] = useState<TagModel | undefined>(undefined);
  const {showBanner, updateBannerOnTagsBulk} = useSettingsBanners();
  const tagsIdMap = useMemo(
    () =>
      _(searchTags)
        .flatten()
        .uniqBy((t) => t.id)
        .keyBy((t) => t.id)
        .value(),
    [searchTags],
  );

  const fetchTags = useCallback(
    async (options: {
      newQuery?: string;
      newOffset?: number;
      newParentTagId?: number | null;
      refreshList?: boolean;
    }) => {
      const {newOffset, newQuery, newParentTagId} = options || {};
      const queryToUse = newQuery ?? searchQuery;
      const parentTagIdToUse = _.isNull(newParentTagId) ? undefined : newParentTagId || listParentTagId;

      const shouldRefreshList =
        (!isUndefined(newQuery) && newQuery !== searchQuery) || newParentTagId !== listParentTagId;

      setStatusMore(AsyncMoreStatusesEnum.LOADING);
      const url = buildTagSearchUrl(company, queryToUse, {
        isInSettingsContext: true,
        namespaces: [tagsUrl.namespace],
        behaviors: [TagBehaviorsEnum.CAN_UPDATE],
        excludeTagIds: props.tagsToActOn.map((t) => t.id),
        offset: newOffset ?? offset,
        parentTagIds: _.compact([parentTagIdToUse]),
        shouldReturnParentsOnly: !parentTagIdToUse && !queryToUse,
        sort: SearchTagSortsEnum.PATH_ASC,
      });

      const tagSearch = await fetchApiAsync(ApiSearchTags, url, {
        cancelToken,
      });

      if (!tagSearch) {
        setStatusMore(AsyncMoreStatusesEnum.ERROR);
        return undefined;
      }

      if (tagSearch.limit === tagSearch.tags.length) {
        setStatusMore(AsyncMoreStatusesEnum.HAS_MORE);
        if (shouldRefreshList) {
          setOffset(tagSearch.limit);
        } else {
          setOffset((prev) => prev + tagSearch.limit);
        }
      } else {
        setStatusMore(AsyncMoreStatusesEnum.ALL_LOADED);
      }

      if (shouldRefreshList) {
        setSearchTags(tagSearch.tags);
      } else {
        setSearchTags((prev) => concat(prev, tagSearch.tags));
      }

      return tagSearch;
    },
    [searchQuery, listParentTagId, company, tagsUrl.namespace, props.tagsToActOn, offset, cancelToken],
  );

  useInitialEffect(() => {
    fetchTags({});
  });

  const onTagClick: HandlerOf<TagModel> = (tag) => {
    const children = getTagChildrenIds(tag, props.isInArchivedView);
    if (newParentTag?.id === tag.id && !children.length) {
      if (tag.parentTagId && tagsIdMap[tag.parentTagId]) {
        setNewParentTag(tagsIdMap[tag.parentTagId]);
      } else {
        setNewParentTag(undefined);
      }
      return;
    }

    if (children.length) {
      setListParentTagId(tag.id);
      fetchTags({
        newParentTagId: tag.id,
        newOffset: 0,
      });
    }
    setNewParentTag(tag);
  };

  const onNestUnderClick = () => {
    if (hasAllSelected) {
      return props.tagsBulkAction({
        type: TagBulkActionTypesEnum.NEST_UNDER,
        namespace: tagsUrl.namespace,
        parentTagId: newParentTag?.id,
        searchTerm: props.searchQuery || '',
        searchOptions: {
          behaviors: [TagBehaviorsEnum.CAN_UPDATE],
          excludeTagIds: props.tagsToActOn.map((t) => t.id),
          isArchived: false,
          parentTagId: listParentTagId,
        },
      });
    }

    return props.tagsBulkAction({
      type: TagBulkActionTypesEnum.NEST_UNDER,
      namespace: tagsUrl.namespace,
      parentTagId: newParentTag?.id,
      selectedTagIds: tagsToActOn.map((t) => t.id),
    });
  };

  const onOkNestClick = async () => {
    await onNestUnderClick();
    if (newParentTag && !hasAllSelected) {
      showBanner({
        type: SettingsBannersEnum.LOADING,
        message: computeLoadingBannerMessage(props, newParentTag),
        dismissAfter: 30 * 1000,
      });
      updateBannerOnTagsBulk({
        type: SettingsBannersEnum.SUCCESS,
        message: computeSuccessBannerMessage(props, newParentTag),
      });
    }

    props.onNest();
  };

  function renderIcon(tag: TagModel) {
    const hasChildren = getTagChildrenIds(tag, props.isInArchivedView).length > 0;
    const isTagSelected = tag.id === newParentTag?.id && !hasChildren;

    if (isTagSelected) {
      return <Icon name="checkmarkSelectedSmall" color={theme.palette.blue.shade40} />;
    }

    if (hasChildren) {
      return <Icon name="chevronRightSmall" color={theme.greys.shade60} />;
    }

    return null;
  }

  function renderTag(tag: TagModel) {
    const hasChildren = getTagChildrenIds(tag, props.isInArchivedView).length > 0;
    const isTagSelected = tag.id === newParentTag?.id && !hasChildren;
    return (
      <SettingsResourceDialogListItem
        onClick={() => onTagClick(tag)}
        iconPart={renderHighlightBadge(tag)}
        isSelected={isTagSelected}
        rightIconPart={renderIcon(tag)}
        title={tag.name}
        subtitle={
          hasChildren ? (
            <Plural
              id="settingstable-nestedtags"
              value={getTagChildrenIds(tag, props.isInArchivedView).length}
              one="# nested tag"
              other="# nested tags"
            />
          ) : null
        }
      />
    );
  }

  const moreStatuses = useMemo(
    () => ({
      [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
      [ScrollDirectionsEnum.BOTTOM]: statusMore,
    }),
    [statusMore],
  );

  const cleanedTags = useMemo(
    () =>
      _(searchTags)
        .map((s) => s[0])
        .filter((t) => {
          if (searchQuery) {
            return true;
          }

          return Boolean(!t.parentTagId || t.parentTagId === listParentTagId);
        })
        .value(),
    [listParentTagId, searchQuery, searchTags],
  );

  const tagIds = useMemo(
    () =>
      _(cleanedTags)
        .map((t) => t.id)
        .uniq()
        .value(),
    [cleanedTags],
  );

  return (
    <SettingsResourceListDialog
      itemIds={tagIds}
      onRequestClose={props.onRequestClose}
      renderItem={(id) => {
        const tag = tagsIdMap[id];

        if (!tag) {
          return null;
        }

        return renderTag(tag);
      }}
      title={
        <Plural value={props.tagsToActOn.length} one="Nest 1 item under..." other="Nest # items under..." />
      }
      confirmLabel={<Trans id="nestunderdialog-nest">Nest</Trans>}
      onConfirmClick={onOkNestClick}
      emptyImageSource={emptyTag}
      emptyLabel={<Trans id="nestunderdialog-notags">No tags</Trans>}
      headerActionLabel={<Trans id="settingstagsnest-addtag">Add tag</Trans>}
      onHeaderActionClick={props.onAddTagClick}
      moreStatuses={moreStatuses}
      searchQuery={searchQuery}
      onChange={(newQuery) => {
        setSearchQuery(newQuery);
        fetchTags({newQuery});
      }}
      onLoadMore={() => fetchTags({})}
      topline={
        <SettingsTagsNestUnderDialogToplineContainer
          onTagClick={(tag) => {
            setListParentTagId(tag?.id);
            setNewParentTag(tag);

            fetchTags({
              newParentTagId: !tag ? null : tag.id,
              newOffset: 0,
            });
          }}
          tagId={listParentTagId}
        />
      }
    />
  );
};

function computeSuccessBannerMessage(props: SettingsTagsNestUnderDialogProps, newParentTag: TagModel) {
  const {tagsToActOn} = props;

  if (tagsToActOn.length === 0) {
    return null;
  }

  if (tagsToActOn.length === 1) {
    return (
      <Trans id="settingstags-nestsingle-success">
        Nested &quot;{tagsToActOn[0].name}&quot; under &quot;{newParentTag.name}&quot;
      </Trans>
    );
  }

  return (
    <Trans id="settingstags-nestmany-success">
      Nested {tagsToActOn.length} tags under &quot;{newParentTag.name}&quot;
    </Trans>
  );
}
function computeLoadingBannerMessage(props: SettingsTagsNestUnderDialogProps, newParentTag: TagModel) {
  const {tagsToActOn} = props;

  if (tagsToActOn.length === 0) {
    return null;
  }

  if (tagsToActOn.length === 1) {
    return (
      <Trans id="settingstags-nestsingle-loading">
        Nesting &quot;{tagsToActOn[0].name}&quot; under &quot;{newParentTag.name}&quot;
      </Trans>
    );
  }

  return (
    <Trans id="settingstags-nestmany-loading">
      Nesting {tagsToActOn.length} tags under &quot;{newParentTag.name}&quot;
    </Trans>
  );
}

function renderHighlightBadge(tag: TagModel) {
  if (tag.type === HighlightTypesEnum.EMOJI) {
    const definition = getTagEmojiHighlight(tag);
    if (definition) {
      return (
        <StyledBoxedEmoji>
          <SheetEmoji emoji={definition.emoji} skinTone={definition.skinTone} size={16} />
        </StyledBoxedEmoji>
      );
    }
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <BoxedIcon
          name="tagFilledSmall"
          backgroundColor={theme.greys.white}
          color={
            tag.type === HighlightTypesEnum.COLOR
              ? theme.palette[tag.highlightColor].shade40
              : theme.greys.shade70
          }
          borderColor={theme.alphas.gray10}
          borderStyle="solid"
          size={VisualSizesEnum.SMALL}
        />
      )}
    </UserThemeConsumer>
  );
}

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  tagsBulkAction,
  navigateToLink: navigateToLinkAction,
}));

export const SettingsTagsNestUnderDialogContainer = connect(
  null,
  mapDispatchToProps,
)(SettingsTagsNestUnderDialog);
